import {
  Table,
  Badge,
  Text,
  HStack,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, MoreVertical, Eye, Trash2, Star } from 'lucide-react'
import type { OwnerWithUnits } from '../types'

type SortField = 'last_name' | 'first_name' | 'email' | 'units_count'
type SortDirection = 'asc' | 'desc'

interface OwnerTableProps {
  owners: OwnerWithUnits[]
  selectedOwnerId: string | null
  onOwnerClick: (id: string) => void
  onDelete?: (id: string) => void
  canDelete?: (ownerId: string) => boolean
  sortField?: SortField
  sortDirection?: SortDirection
  onSort?: (field: SortField) => void
}

export function OwnerTable({
  owners,
  selectedOwnerId,
  onOwnerClick,
  onDelete,
  canDelete,
  sortField,
  sortDirection,
  onSort,
}: OwnerTableProps) {
  const { t } = useTranslation()

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <ArrowUp size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    ) : (
      <ArrowDown size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    )
  }

  const isEmailPreferred = (contact: string | null) => contact === 'EMAIL'
  const isPhonePreferred = (contact: string | null) => contact === 'MAIL' || contact === 'COURRIER'

  const getActiveUnitsCount = (owner: OwnerWithUnits) => {
    if (!owner.ownerships) return 0
    return owner.ownerships.filter((o) => !o.end_date).length
  }

  const getUniqueResidences = (owner: OwnerWithUnits) => {
    if (!owner.ownerships || owner.ownerships.length === 0) return []

    return Array.from(
      new Map(
        owner.ownerships
          .filter((o) => o.units?.residences)
          .map((o) => [
            o.units.residences.id,
            {
              id: o.units.residences.id,
              key: o.units.residences.residence_key,
              name: o.units.residences.name,
            },
          ])
      ).values()
    )
  }

  return (
    <Table.Root variant="outline" size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader
            width="180px"
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('last_name')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('owners.fields.name')}
            <SortIcon field="last_name" />
          </Table.ColumnHeader>
          <Table.ColumnHeader
            width="220px"
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('email')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('owners.fields.email')}
            <SortIcon field="email" />
          </Table.ColumnHeader>
          <Table.ColumnHeader width="140px">{t('owners.fields.phone')}</Table.ColumnHeader>
          <Table.ColumnHeader width="220px">Résidences</Table.ColumnHeader>
          <Table.ColumnHeader
            textAlign="center"
            width="80px"
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('units_count')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            Lots
            <SortIcon field="units_count" />
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center" width="80px">{t('common.actions')}</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {owners.map((owner) => {
          const residences = getUniqueResidences(owner)
          return (
            <Table.Row
              key={owner.id}
              data-owner-row={owner.id}
              onClick={() => onOwnerClick(owner.id)}
              cursor="pointer"
              bg={selectedOwnerId === owner.id ? 'brand.50' : undefined}
              _hover={{
                bg: selectedOwnerId === owner.id ? 'brand.100' : 'gray.50',
              }}
            >
              <Table.Cell>
                <Text fontWeight="medium">
                  {owner.first_name} {owner.last_name}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={2}>
                  <Text fontSize="sm">
                    {owner.email || '-'}
                  </Text>
                  {isEmailPreferred(owner.preferred_contact) && owner.email && (
                    <Star size={14} />
                  )}
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={2}>
                  <Text fontSize="sm">
                    {owner.phone || '-'}
                  </Text>
                  {isPhonePreferred(owner.preferred_contact) && owner.phone && (
                    <Star size={14} />
                  )}
                </HStack>
              </Table.Cell>
              <Table.Cell>
                {residences.length > 0 ? (
                  <HStack gap={1} flexWrap="wrap">
                    {residences.map((residence) => (
                      <Badge key={residence.id} size="sm" colorPalette="gray" variant="subtle">
                        {residence.key ? (
                          <>
                            <Text as="span" fontWeight="bold">
                              {residence.key}
                            </Text>{' '}
                            {residence.name}
                          </>
                        ) : (
                          residence.name
                        )}
                      </Badge>
                    ))}
                  </HStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    -
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                <Text fontSize="sm" fontWeight="medium">
                  {getActiveUnitsCount(owner)}
                </Text>
              </Table.Cell>
              <Table.Cell onClick={(e) => e.stopPropagation()}>
                <HStack justify="center">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.actions')}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem
                            value="details"
                            onClick={(e) => {
                              e.stopPropagation()
                              onOwnerClick(owner.id)
                            }}
                          >
                            <HStack gap={2}>
                              <Eye size={16} />
                              <Text>{t('common.details')}</Text>
                            </HStack>
                          </MenuItem>
                          {onDelete && canDelete?.(owner.id) && (
                            <MenuItem
                              value="delete"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDelete(owner.id)
                              }}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>{t('common.delete')}</Text>
                              </HStack>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </HStack>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
